import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import LinearProgress from '@mui/material/LinearProgress';
import Divider from '@mui/material/Divider';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Project } from '../../types';

interface ProjectStatusChartProps {
  projects: Project[];
}

const fmt = (n: number) =>
  n >= 1_000_000
    ? `$${(n / 1_000_000).toFixed(1)}M`
    : n >= 1_000
    ? `$${(n / 1_000).toFixed(0)}K`
    : `$${n}`;

export default function ProjectStatusChart({ projects }: ProjectStatusChartProps) {
  const theme = useTheme();

  const totalBudget = projects.reduce((s, p) => s + (p.budgetedAmount || 0), 0);
  const totalSpent  = projects.reduce((s, p) => s + (p.spentAmount  || 0), 0);
  const remaining   = totalBudget - totalSpent;
  const utilPct     = totalBudget > 0 ? Math.round((totalSpent / totalBudget) * 100) : 0;

  const barData = projects.map(p => ({
    name: p.name.length > 14 ? p.name.slice(0, 13) + '…' : p.name,
    Budget: p.budgetedAmount || 0,
    Spent:  p.spentAmount    || 0,
  }));

  const overBudget = projects.filter(p => (p.spentAmount || 0) > (p.budgetedAmount || 0)).length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 0.5 }}>
      <Typography component="h2" variant="subtitle2" sx={{ fontWeight: 700, flexShrink: 0 }}>
        Finance Overview
      </Typography>
      <Card variant="outlined" sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <CardContent sx={{ flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: '14px !important', '&:last-child': { pb: '14px !important' } }}>

          {/* KPI row */}
        <Stack direction="row" divider={<Divider orientation="vertical" flexItem />} spacing={1} sx={{ mb: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Total Budget</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{fmt(totalBudget)}</Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Spent</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: utilPct > 90 ? theme.palette.error.main : theme.palette.text.primary }}>
              {fmt(totalSpent)}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Remaining</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: remaining < 0 ? theme.palette.error.main : theme.palette.success.main }}>
              {fmt(Math.abs(remaining))}{remaining < 0 ? ' over' : ''}
            </Typography>
          </Box>
        </Stack>

        {/* Utilization bar */}
        <Box sx={{ mb: 1 }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">Budget utilisation</Typography>
            <Typography variant="caption" sx={{ fontWeight: 600, color: utilPct > 90 ? theme.palette.error.main : 'text.primary' }}>
              {utilPct}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={Math.min(utilPct, 100)}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: theme.palette.grey[200],
              '& .MuiLinearProgress-bar': {
                bgcolor: utilPct > 90 ? theme.palette.error.main : utilPct > 70 ? theme.palette.warning.main : theme.palette.success.main,
              },
            }}
          />
          {overBudget > 0 && (
            <Typography variant="caption" color="error">{overBudget} project{overBudget > 1 ? 's' : ''} over budget</Typography>
          )}
        </Box>

        {/* Budget vs Spent bar chart */}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData} margin={{ top: 4, right: 4, left: -10, bottom: 0 }} barSize={10}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: theme.palette.text.secondary }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={(v) => fmt(v)} tick={{ fontSize: 10, fill: theme.palette.text.secondary }} axisLine={false} tickLine={false} />
              <Tooltip
                formatter={(value: number) => fmt(value)}
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Budget" fill={theme.palette.primary.main} fillOpacity={0.4} radius={[3, 3, 0, 0]} />
              <Bar dataKey="Spent"  fill={theme.palette.primary.main} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Box>

        </CardContent>
      </Card>
    </Box>
  );
}
